// biome-ignore-all lint/a11y: TODO - address a11y issues later
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  CustomChannel,
  CustomChannelMetrics,
} from "@/hooks/api/use-custom-channels";
import { DEFAULT_PRIMARY_COLUMN_WIDTHS } from "@/lib/column-defaults";
import { getFilterType } from "@/lib/metric-types";
import { formatMetricValue } from "@/lib/metrics/metric-formatting";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";
import {
  METRIC_CONFIG,
  METRIC_DATA_TYPES,
} from "./custom-channels-metrics-config";

/**
 * Extended custom channel column type with category and style information
 */
export interface CustomChannelColumn extends UnifiedTableColumn<CustomChannel> {
  category?: string;
  categoryStyle?: string;
}

export const getCustomChannelColumns = (
  selectedMetrics?: Array<keyof CustomChannelMetrics>,
  onMetricClick?: (
    channelName: string,
    metric: keyof CustomChannelMetrics,
    subchannelName?: string,
    metricValue?: number | string,
  ) => void,
  isGrouped: boolean = true, // New parameter to determine if showing grouped or ungrouped view
): CustomChannelColumn[] => {
  // Base columns for custom channels
  const baseColumns: UnifiedTableColumn<CustomChannel>[] = [
    {
      key: "custom_channel",
      title: "Custom Channel",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.SMALL,
      required: true, // Primary column that cannot be hidden
      render: (channel: CustomChannel) => {
        // In grouped mode, show subchannel name if present, otherwise show channel name
        // In ungrouped mode, always show channel name only
        const displayName = isGrouped
          ? channel.custom_subchannel || channel.custom_channel
          : channel.custom_channel;

        // Build URL with subchannel parameter if present (in grouped mode)
        let explorerUrl = `/custom-channels/${encodeURIComponent(
          channel.custom_channel,
        )}/explorer`;
        if (isGrouped && channel.custom_subchannel) {
          const searchParams = new URLSearchParams();
          searchParams.set("subchannel", channel.custom_subchannel);
          explorerUrl += `?${searchParams.toString()}`;
        }

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 w-full">
                <Link
                  to={explorerUrl}
                  className="text-foreground p-4 rounded hover:bg-accent transition-colors block flex-1"
                  title={displayName}
                >
                  {displayName}
                </Link>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>View in Custom Channels Explorer</p>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
  ];

  // Add Sub-Channel column when in ungrouped mode
  if (!isGrouped) {
    baseColumns.push({
      key: "custom_subchannel",
      title: "Sub-Channel",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 200,
      render: (channel: CustomChannel) => {
        const subchannelName = channel.custom_subchannel || "-";

        // If there's no subchannel, just show a dash
        if (!channel.custom_subchannel) {
          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-muted-foreground p-4 block flex-1">-</span>
            </div>
          );
        }

        // For subchannels, create a link to the explorer with subchannel pre-selected
        // We'll use URL params and the subchannel selector will handle setting the sessionStorage
        const searchParams = new URLSearchParams();
        searchParams.set("subchannel", channel.custom_subchannel);

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 w-full">
                <Link
                  to={`/custom-channels/${encodeURIComponent(
                    channel.custom_channel,
                  )}/explorer?${searchParams.toString()}`}
                  className="text-foreground p-4 rounded hover:bg-accent transition-colors block flex-1"
                  title={subchannelName}
                >
                  {subchannelName}
                </Link>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>View in Custom Channels Explorer</p>
            </TooltipContent>
          </Tooltip>
        );
      },
    });
  }

  // Add metric columns if selectedMetrics is provided
  if (selectedMetrics && selectedMetrics.length > 0) {
    const metricColumns = selectedMetrics.map((metricKey) => {
      const metricConfig = METRIC_CONFIG[metricKey];
      const dataType = METRIC_DATA_TYPES[metricKey];

      return {
        key: `metric_${metricKey}`,
        title: metricConfig?.title ?? metricKey,
        tooltip: metricConfig?.tooltip,
        columnType: "metric",
        sortable: true,
        filterable: true,
        filterType: getFilterType(dataType ?? "default"),
        nullSort: "last",
        defaultSortDirection: "desc",
        render: (
          channel: CustomChannel & {
            metrics?: Record<
              keyof CustomChannelMetrics,
              number | string | undefined
            >;
          },
        ) => {
          if (!channel.metrics) {
            return <Skeleton className="h-4 w-12" />;
          }

          const metricValue = channel.metrics?.[metricKey];
          if (metricValue === undefined || metricValue === null) {
            return <span className="text-muted-foreground">-</span>;
          }

          // Use data type from METRIC_DATA_TYPES for rendering
          const dataType = METRIC_DATA_TYPES[metricKey] ?? "default";

          const formattedContent = (
            <span className="font-medium">
              {formatMetricValue(metricValue, dataType)}
            </span>
          );

          // Check if this metric supports drill-down
          const supportsClickableDrilldown =
            metricConfig?.drilldownColumns &&
            metricConfig?.drilldownColumns.length > 0;

          const clickableContent =
            onMetricClick && supportsClickableDrilldown ? (
              <button
                className="w-full h-full text-left p-0 hover:underline decoration-2 underline-offset-4 transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onMetricClick(
                    channel.custom_channel,
                    metricKey,
                    channel.custom_subchannel,
                    metricValue,
                  );
                }}
              >
                {formattedContent}
              </button>
            ) : (
              formattedContent
            );

          return clickableContent;
        },
        category: metricConfig?.category,
        categoryStyle: metricConfig?.category
          ? CATEGORY_STYLES[metricConfig.category]?.color
          : undefined,
      } as UnifiedTableColumn<CustomChannel>;
    });

    return [...baseColumns, ...metricColumns];
  }

  return baseColumns;
};

// Helper: convert UnifiedTableColumn list to TanStack ColumnDef list for useReactTable
export const getCustomChannelColumnDefs = (
  selectedMetrics?: Array<keyof CustomChannelMetrics>,
  onMetricClick?: (
    channelName: string,
    metric: keyof CustomChannelMetrics,
    subchannelName?: string,
    metricValue?: number | string,
  ) => void,
  isGrouped: boolean = true,
): ColumnDef<CustomChannel>[] => {
  const unifiedColumns = getCustomChannelColumns(
    selectedMetrics,
    onMetricClick,
    isGrouped,
  );

  // Exclude columns that are for export only
  return unifiedColumns
    .filter((col) => !col.exportOnly)
    .map((col): ColumnDef<CustomChannel> => {
      const colId = col.key.toString();

      // Basic cell renderer: use provided render or raw value
      const cellRenderer = col.render
        ? (info: {
            row: {
              original: CustomChannel;
              getCanExpand: () => boolean;
              getIsExpanded: () => boolean;
              getToggleExpandedHandler: () => () => void;
            };
          }) => {
            const { row } = info;
            const canExpand = row.getCanExpand();
            const isExpanded = row.getIsExpanded();

            // For the custom_channel column, add chevron for expandable rows
            if (colId === "custom_channel" && canExpand) {
              return (
                <div className="flex items-center gap-2 w-full">
                  <button
                    onClick={row.getToggleExpandedHandler()}
                    className="p-1 hover:bg-accent rounded transition-colors"
                    aria-label={
                      isExpanded ? "Collapse subchannels" : "Expand subchannels"
                    }
                  >
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 transition-transform",
                        isExpanded && "rotate-90",
                      )}
                    />
                  </button>
                  {typeof col.render === "function"
                    ? col.render(row.original)
                    : "-"}
                </div>
              );
            }

            return typeof col.render === "function"
              ? col.render(row.original)
              : "-";
          }
        : (info: { getValue: () => unknown }) => {
            const val = info.getValue();
            return val === null || val === undefined || val === ""
              ? "-"
              : (val as React.ReactNode);
          };

      // Use column size
      const size = col.size;

      return {
        id: colId,
        accessorKey: typeof col.key === "string" ? col.key : undefined,
        header: () => col.title,
        cell: cellRenderer,
        enableSorting: !!col.sortable,
        size,
        meta: {
          title: col.title,
          categoryStyle: col.categoryStyle,
          sortable: col.sortable,
          filterable: col.filterable,
          filterType: col.filterType,
          filterOptions: col.filterOptions,
          defaultSortDirection: col.defaultSortDirection,
          nullSort: col.nullSort,
          required: col.required,
        },
      } as ColumnDef<CustomChannel>;
    });
};
